import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Package,
  Filter,
  RefreshCw,
  Eye,
  Image as ImageIcon,
  TrendingDown,
  TrendingUp,
  Star
} from "lucide-react";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { productService, Product, ProductFilters, ProductDetails, CreateProductRequest, UpdateProductRequest } from "@/services/productService";
import { categoryService } from "@/services/categoryService";
import { brandService } from "@/services/brandService";

export default function Products() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string>('all');
  const [sort, setSort] = useState<string>('newest');
  const [categoryId, setCategoryId] = useState<string>('all');
  const [brandId, setBrandId] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [stockValue, setStockValue] = useState<string>('');
  
  // Form state for create
  const [createFormData, setCreateFormData] = useState<CreateProductRequest>({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    price: 0,
    discount_price: null,
    stock: 0,
    sku: '',
    barcode: '',
    category_id: 0,
    brand_id: '',
    status: 'active',
    is_featured: false,
    thumbnail_url: null,
    images: [],
  });
  
  // Form state for edit
  const [editFormData, setEditFormData] = useState<UpdateProductRequest>({});

  // Image upload state
  const [createThumbUploading, setCreateThumbUploading] = useState(false);
  const [editThumbUploading, setEditThumbUploading] = useState(false);
  const [createImgUploading, setCreateImgUploading] = useState(false);
  const [editImgUploading, setEditImgUploading] = useState(false);

  // Upload a single image file and return its URL
  const uploadImageFile = async (file: File): Promise<string | null> => {
    const res = await productService.uploadImage(file);
    if (res.success && res.data?.url) return res.data.url;
    toast({ title: res.message || 'Image upload failed', variant: 'destructive' });
    return null;
  };

  // Build filters object for API
  const filters: ProductFilters = useMemo(() => {
    const filterObj: ProductFilters = {
      page: currentPage,
      limit: limit,
      sort: sort !== 'all' ? sort : undefined
    };
    if (search) filterObj.q = search;
    if (status !== 'all') filterObj.status = status;
    if (categoryId !== 'all') filterObj.category_id = categoryId;
    if (brandId !== 'all') filterObj.brand_id = brandId;
    return filterObj;
  }, [currentPage, limit, search, status, sort, categoryId, brandId]);

  // React Query for products data
  const { data: productsResponse, isLoading: productsLoading, error: productsError, refetch: refetchProducts } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => productService.getProducts(filters),
    staleTime: 30000, // 30 seconds
  });

  // React Query for categories (for filter dropdown)
  const { data: categoriesResponse } = useQuery({
    queryKey: ['categoriesForFilter'],
    queryFn: () => categoryService.getCategories({ page: 1, limit: 1000 }),
    staleTime: 300000, // 5 minutes
  });

  // React Query for brands (for filter dropdown)
  const { data: brandsResponse } = useQuery({
    queryKey: ['brandsForFilter'],
    queryFn: () => brandService.getBrands({ page: 1, limit: 1000 }),
    staleTime: 300000, // 5 minutes
  });

  // Extract data from responses
  const products = useMemo(() => {
    return productsResponse?.success && productsResponse?.data ? productsResponse.data.items : [];
  }, [productsResponse]);

  const pagination = useMemo(() => {
    return productsResponse?.success && productsResponse?.data ? {
      total: productsResponse.data.total,
      totalPages: productsResponse.data.totalPages,
      currentPage: productsResponse.data.page,
      perPage: productsResponse.data.pageSize
    } : {
      total: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 20
    };
  }, [productsResponse]);

  const categories = useMemo(() => {
    return categoriesResponse?.success && categoriesResponse?.data ? categoriesResponse.data.items : [];
  }, [categoriesResponse]);

  const brands = useMemo(() => {
    return brandsResponse?.success && brandsResponse?.data ? brandsResponse.data.items : [];
  }, [brandsResponse]);

  const isLoading = productsLoading;

  // React Query for product details
  const { data: productDetailsResponse, isLoading: detailsLoading } = useQuery({
    queryKey: ['productDetails', selectedProduct?.id],
    queryFn: () => productService.getProductById(selectedProduct!.id),
    enabled: !!selectedProduct && isViewDialogOpen,
    staleTime: 30000,
  });

  const productDetails = useMemo(() => {
    return productDetailsResponse?.success && productDetailsResponse?.data 
      ? productDetailsResponse.data 
      : null;
  }, [productDetailsResponse]);

  // Mutations
  const createProductMutation = useMutation({
    mutationFn: (data: CreateProductRequest) => productService.createProduct(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsCreateDialogOpen(false);
      setCreateFormData({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        price: 0,
        discount_price: null,
        stock: 0,
        sku: '',
        barcode: '',
        category_id: 0,
        brand_id: '',
        status: 'active',
        is_featured: false,
        thumbnail_url: null,
        images: [],
      });
      toast({
        title: t("success") || "Success",
        description: t("productCreated") || "Product created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToCreateProduct") || "Failed to create product",
        variant: "destructive",
      });
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductRequest }) =>
      productService.updateProduct(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['productDetails'] });
      setIsEditDialogOpen(false);
      setEditFormData({});
      setSelectedProduct(null);
      toast({
        title: t("success") || "Success",
        description: t("productUpdated") || "Product updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToUpdateProduct") || "Failed to update product",
        variant: "destructive",
      });
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: (id: string) => productService.deleteProduct(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast({
        title: t("success") || "Success",
        description: t("productDeleted") || "Product deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToDeleteProduct") || "Failed to delete product",
        variant: "destructive",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'inactive' }) =>
      productService.updateProductStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['productDetails'] });
      toast({
        title: t("success") || "Success",
        description: t("productStatusUpdated") || "Product status updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToUpdateProductStatus") || "Failed to update product status",
        variant: "destructive",
      });
    },
  });

  const updateStockMutation = useMutation({
    mutationFn: ({ id, stock }: { id: string; stock: number }) =>
      productService.updateProductStock(id, stock),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['productDetails'] });
      setIsStockDialogOpen(false);
      setStockValue('');
      setSelectedProduct(null);
      toast({
        title: t("success") || "Success",
        description: t("productStockUpdated") || "Product stock updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToUpdateProductStock") || "Failed to update product stock",
        variant: "destructive",
      });
    },
  });

  // Helper function to get display name based on current language
  const getDisplayName = (item: { name: string; name_ar: string }) => {
    return i18n.language === 'ar' ? item.name_ar : item.name;
  };

  // Helper function to parse images JSON string
  const parseImages = (imagesString: string | null): string[] => {
    if (!imagesString) return [];
    try {
      return JSON.parse(imagesString);
    } catch {
      return [];
    }
  };

  // Handle filter changes - reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value: string) => {
    setSort(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryId(value);
    setCurrentPage(1);
  };

  const handleBrandChange = (value: string) => {
    setBrandId(value);
    setCurrentPage(1);
  };

  // Event handlers
  const handleViewProduct = (product: Product) => {
    setSelectedProduct(product);
    setIsViewDialogOpen(true);
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    // Load product details if not already loaded
    productService.getProductById(product.id).then((response) => {
      if (response.success && response.data) {
        const details = response.data;
        const imagesArray = parseImages(details.images);
        setEditFormData({
          name: details.name,
          name_ar: details.name_ar,
          description: details.description,
          description_ar: details.description_ar,
          price: parseFloat(details.price),
          discount_price: details.discount_price ? parseFloat(details.discount_price) : null,
          stock: details.stock,
          sku: details.sku,
          barcode: details.barcode,
          category_id: details.category_id,
          brand_id: details.brand_id,
          status: details.status,
          is_featured: details.is_featured,
          thumbnail_url: details.thumbnail_url,
          images: imagesArray,
        });
        setIsEditDialogOpen(true);
      }
    });
  };

  const handleCreateProduct = () => {
    if (!createFormData.name || !createFormData.name_ar || !createFormData.sku || !createFormData.category_id || !createFormData.brand_id) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("pleaseCompleteAllRequiredFields") || "Please complete all required fields",
        variant: "destructive",
      });
      return;
    }
    createProductMutation.mutate(createFormData);
  };

  const handleSaveEdit = () => {
    if (!selectedProduct) return;
    updateProductMutation.mutate({ id: selectedProduct.id, data: editFormData });
  };

  const handleAddImage = (isCreate: boolean = true) => {
    const url = prompt(t("enterImageUrl") || "Enter image URL:");
    if (url) {
      if (isCreate) {
        setCreateFormData(prev => ({
          ...prev,
          images: [...(prev.images || []), url]
        }));
      } else {
        setEditFormData(prev => ({
          ...prev,
          images: [...(prev.images || []), url]
        }));
      }
    }
  };

  const handleRemoveImage = (index: number, isCreate: boolean = true) => {
    if (isCreate) {
      setCreateFormData(prev => ({
        ...prev,
        images: prev.images?.filter((_, i) => i !== index) || []
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        images: prev.images?.filter((_, i) => i !== index) || []
      }));
    }
  };

  const handleDeleteProduct = (product: Product) => {
    const confirmMessage = t("confirmDeleteProduct") || `Are you sure you want to delete product "${product.name}"?`;
    if (confirm(confirmMessage)) {
      deleteProductMutation.mutate(product.id);
    }
  };

  const handleProductStatusChange = (product: Product, newStatus: 'active' | 'inactive') => {
    updateStatusMutation.mutate({ id: product.id, status: newStatus });
  };

  const handleUpdateStock = (product: Product) => {
    setSelectedProduct(product);
    setStockValue(product.stock.toString());
    setIsStockDialogOpen(true);
  };

  const handleSaveStock = () => {
    if (!selectedProduct) return;
    const stock = parseInt(stockValue);
    if (isNaN(stock) || stock < 0) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("pleaseEnterValidStock") || "Please enter a valid stock number",
        variant: "destructive",
      });
      return;
    }
    updateStockMutation.mutate({ id: selectedProduct.id, stock });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("productsManage") || "Products Manage"}</h1>
          <p className="text-muted-foreground mt-1">
            {t("manageProducts") || "Manage your products"}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("addProduct") || "Add Product"}
        </Button>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            {t("searchAndFilters") || "Search and Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="search">{t("search") || "Search"}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder={t("searchProductsPlaceholder") || "Search by name, SKU..."}
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("sortBy") || "Sort By"}</Label>
              <Select value={sort || 'newest'} onValueChange={handleSortChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectSort") || "Select Sort"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t("newest") || "Newest"}</SelectItem>
                  <SelectItem value="popular">{t("popular") || "Popular"}</SelectItem>
                  <SelectItem value="price_asc">{t("priceLowToHigh") || "Price: Low to High"}</SelectItem>
                  <SelectItem value="price_desc">{t("priceHighToLow") || "Price: High to Low"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("status") || "Status"}</Label>
              <Select value={status || 'all'} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectStatus") || "Select Status"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                  <SelectItem value="active">{t("active") || "Active"}</SelectItem>
                  <SelectItem value="inactive">{t("inactive") || "Inactive"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("category") || "Category"}</Label>
              <Select value={categoryId || 'all'} onValueChange={handleCategoryChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectCategory") || "Select Category"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allCategories") || "All Categories"}</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {getDisplayName(category)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("brand") || "Brand"}</Label>
              <Select value={brandId || 'all'} onValueChange={handleBrandChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectBrand") || "Select Brand"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allBrands") || "All Brands"}</SelectItem>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {getDisplayName(brand)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("pageSize") || "Items per page"}</Label>
              <Select value={limit.toString()} onValueChange={(value) => {
                setLimit(Math.min(parseInt(value), 100));
                setCurrentPage(1);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("products") || "Products"} ({isLoading ? "..." : pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("image") || "Image"}</TableHead>
                <TableHead>{t("productName") || "Name"}</TableHead>
                <TableHead>{t("sku") || "SKU"}</TableHead>
                <TableHead>{t("category") || "Category"}</TableHead>
                <TableHead>{t("brand") || "Brand"}</TableHead>
                <TableHead>{t("price") || "Price"}</TableHead>
                <TableHead>{t("stock") || "Stock"}</TableHead>
                <TableHead>{t("status") || "Status"}</TableHead>
                <TableHead className="text-right">{t("actions") || "Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {t("loading")}...
                    </div>
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {t("noProductsFound") || "No products found"}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>
                      {product.thumbnail_url ? (
                        <img 
                          src={product.thumbnail_url} 
                          alt={product.name}
                          className="h-12 w-12 object-cover rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-12 w-12 bg-muted rounded flex items-center justify-center">
                          <ImageIcon className="h-6 w-6 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{product.name}</div>
                        <div className="text-sm text-muted-foreground">{product.name_ar}</div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{product.sku}</TableCell>
                    <TableCell>
                      {product.category ? getDisplayName(product.category) : '-'}
                    </TableCell>
                    <TableCell>
                      {product.brand ? getDisplayName(product.brand) : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className={product.discount_price ? "line-through text-muted-foreground text-sm" : "font-medium"}>
                          {product.price.toFixed(2)} {t("currency") || "SDG"}
                        </span>
                        {product.discount_price && (
                          <span className="font-medium text-red-600">
                            {product.discount_price.toFixed(2)} {t("currency") || "SDG"}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant={product.stock < 10 ? "destructive" : "default"}>
                          {product.stock}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUpdateStock(product)}
                          className="h-6 px-2 text-xs"
                          title={t("updateStock") || "Update Stock"}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select
                          value={product.status}
                          onValueChange={(value: 'active' | 'inactive') => handleProductStatusChange(product, value)}
                        >
                          <SelectTrigger className="h-7 w-auto">
                            <SelectValue>
                              <Badge variant={product.status === "active" ? "default" : "secondary"}>
                                {product.status === "active" ? t("active") || "Active" : t("inactive") || "Inactive"}
                              </Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">{t("active") || "Active"}</SelectItem>
                            <SelectItem value="inactive">{t("inactive") || "Inactive"}</SelectItem>
                          </SelectContent>
                        </Select>
                        {product.is_featured && (
                          <Badge variant="outline">
                            <Star className="h-3 w-3 mr-1" />
                            {t("featured") || "Featured"}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("viewProduct") || "View Product"}
                          onClick={() => handleViewProduct(product)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("editProduct") || "Edit Product"}
                          onClick={() => handleEditProduct(product)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("deleteProduct") || "Delete Product"}
                          onClick={() => handleDeleteProduct(product)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                {t("showing") || "Showing"} {(pagination.currentPage - 1) * pagination.perPage + 1}–{Math.min(pagination.currentPage * pagination.perPage, pagination.total)} {t("of") || "of"} {pagination.total} {t("products") || "products"}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  {t("previous") || "Prev"}
                </Button>

                {/* Page numbers — hidden on mobile, shown on sm+ */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className="w-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                {/* Mobile: show current / total */}
                <span className="sm:hidden text-sm px-2 text-muted-foreground">
                  {currentPage} / {pagination.totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages || isLoading}
                >
                  {t("next") || "Next"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Product Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("productDetails") || "Product Details"}</DialogTitle>
            <DialogDescription>
              {t("viewProductInformation") || "View detailed product information"}
            </DialogDescription>
          </DialogHeader>
          
          {productDetails ? (
            <div className="space-y-6">
              {/* Product Header */}
              <div className="flex items-start space-x-4">
                {productDetails.thumbnail_url ? (
                  <img 
                    src={productDetails.thumbnail_url} 
                    alt={productDetails.name}
                    className="h-24 w-24 object-cover rounded border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="h-24 w-24 bg-muted rounded flex items-center justify-center border">
                    <ImageIcon className="h-12 w-12 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1">
                  <h3 className="text-xl font-semibold">{productDetails.name}</h3>
                  <p className="text-muted-foreground">{productDetails.name_ar}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant={productDetails.status === "active" ? "default" : "secondary"}>
                      {productDetails.status === "active" ? t("active") || "Active" : t("inactive") || "Inactive"}
                    </Badge>
                    {productDetails.is_featured && (
                      <Badge variant="outline">
                        <Star className="h-3 w-3 mr-1" />
                        {t("featured") || "Featured"}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Product Images */}
              {parseImages(productDetails.images).length > 0 && (
                <div className="space-y-2">
                  <Label>{t("productImages") || "Product Images"}</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {parseImages(productDetails.images).map((imageUrl, index) => (
                      <img 
                        key={index}
                        src={imageUrl} 
                        alt={`${productDetails.name} - ${index + 1}`}
                        className="h-20 w-20 object-cover rounded border"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Product Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("sku") || "SKU"}</Label>
                  <p className="text-sm font-mono">{productDetails.sku}</p>
                </div>
                <div>
                  <Label>{t("barcode") || "Barcode"}</Label>
                  <p className="text-sm font-mono">{productDetails.barcode}</p>
                </div>
                <div>
                  <Label>{t("category") || "Category"}</Label>
                  <p className="text-sm">{getDisplayName(productDetails.Category)}</p>
                </div>
                <div>
                  <Label>{t("brand") || "Brand"}</Label>
                  <p className="text-sm">{getDisplayName(productDetails.Brand)}</p>
                </div>
                <div>
                  <Label>{t("price") || "Price"}</Label>
                  <div className="flex items-center gap-2">
                    {productDetails.discount_price ? (
                      <>
                        <span className="line-through text-muted-foreground">
                          {parseFloat(productDetails.price).toFixed(2)} {t("currency") || "SDG"}
                        </span>
                        <span className="font-medium text-red-600">
                          {parseFloat(productDetails.discount_price).toFixed(2)} {t("currency") || "SAR"}
                        </span>
                      </>
                    ) : (
                      <span className="font-medium">
                        {parseFloat(productDetails.price).toFixed(2)} {t("currency") || "SAR"}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <Label>{t("stock") || "Stock"}</Label>
                  <p className="text-sm">
                    <Badge variant={productDetails.stock < 10 ? "destructive" : "default"}>
                      {productDetails.stock}
                    </Badge>
                  </p>
                </div>
                <div>
                  <Label>{t("rating") || "Rating"}</Label>
                  <div className="flex items-center gap-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span className="text-sm">{parseFloat(productDetails.rating).toFixed(1)}</span>
                  </div>
                </div>
                <div>
                  <Label>{t("createdAt") || "Created At"}</Label>
                  <p className="text-sm">{new Date(productDetails.createdAt).toLocaleString()}</p>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>{t("description") || "Description"}</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">English</p>
                    <p className="text-sm">{productDetails.description || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">العربية</p>
                    <p className="text-sm">{productDetails.description_ar || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              {t("loading")}...
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsViewDialogOpen(false);
              setSelectedProduct(null);
            }}>
              {t("close") || "Close"}
            </Button>
            {selectedProduct && (
              <Button onClick={() => {
                setIsViewDialogOpen(false);
                handleEditProduct(selectedProduct);
              }}>
                <Edit className="mr-2 h-4 w-4" />
                {t("edit") || "Edit"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("addProduct") || "Add Product"}</DialogTitle>
            <DialogDescription>
              {t("createNewProduct") || "Create a new product"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-name">{t("productName") || "Name"} *</Label>
                <Input
                  id="create-name"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t("enterProductName") || "Enter product name"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-name-ar">{t("productNameAr") || "Name (AR)"} *</Label>
                <Input
                  id="create-name-ar"
                  value={createFormData.name_ar}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                  placeholder={t("enterProductNameAr") || "Enter product name in Arabic"}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-description">{t("description") || "Description"}</Label>
                <Textarea
                  id="create-description"
                  value={createFormData.description}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t("enterDescription") || "Enter description"}
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-description-ar">{t("descriptionAr") || "Description (AR)"}</Label>
                <Textarea
                  id="create-description-ar"
                  value={createFormData.description_ar}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                  placeholder={t("enterDescriptionAr") || "Enter description in Arabic"}
                  rows={3}
                />
              </div>
            </div>

            {/* Pricing & Inventory */}
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-price">{t("price") || "Price"} *</Label>
                <Input
                  id="create-price"
                  type="number"
                  step="0.01"
                  value={createFormData.price || ''}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-discount-price">{t("discountPrice") || "Discount Price"}</Label>
                <Input
                  id="create-discount-price"
                  type="number"
                  step="0.01"
                  value={createFormData.discount_price || ''}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, discount_price: e.target.value ? parseFloat(e.target.value) : null }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-stock">{t("stock") || "Stock"} *</Label>
                <Input
                  id="create-stock"
                  type="number"
                  value={createFormData.stock || ''}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("status") || "Status"}</Label>
                <Select 
                  value={createFormData.status} 
                  onValueChange={(value) => setCreateFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("active") || "Active"}</SelectItem>
                    <SelectItem value="inactive">{t("inactive") || "Inactive"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* SKU & Barcode */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-sku">{t("sku") || "SKU"} *</Label>
                <Input
                  id="create-sku"
                  value={createFormData.sku}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder={t("enterSku") || "Enter SKU"}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-barcode">{t("barcode") || "Barcode"}</Label>
                <Input
                  id="create-barcode"
                  value={createFormData.barcode}
                  onChange={(e) => setCreateFormData(prev => ({ ...prev, barcode: e.target.value }))}
                  placeholder={t("enterBarcode") || "Enter barcode"}
                />
              </div>
            </div>

            {/* Category & Brand */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("category") || "Category"} *</Label>
                <Select 
                  value={createFormData.category_id.toString()} 
                  onValueChange={(value) => setCreateFormData(prev => ({ ...prev, category_id: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectCategory") || "Select Category"} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {getDisplayName(category)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t("brand") || "Brand"} *</Label>
                <Select 
                  value={createFormData.brand_id} 
                  onValueChange={(value) => setCreateFormData(prev => ({ ...prev, brand_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("selectBrand") || "Select Brand"} />
                  </SelectTrigger>
                  <SelectContent>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={brand.id}>
                        {getDisplayName(brand)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Thumbnail upload */}
            <div className="space-y-2">
              <Label>{t("thumbnailUrl") || "Thumbnail Image"}</Label>
              <div className="flex items-center gap-3">
                {createFormData.thumbnail_url && (
                  <img src={createFormData.thumbnail_url} alt="thumbnail" className="h-16 w-16 object-cover rounded border" />
                )}
                <label className="flex-1">
                  <div className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-muted transition-colors ${createThumbUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <ImageIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {createThumbUploading ? 'Uploading...' : createFormData.thumbnail_url ? 'Change image' : 'Choose image'}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={createThumbUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setCreateThumbUploading(true);
                      const url = await uploadImageFile(file);
                      if (url) setCreateFormData(prev => ({ ...prev, thumbnail_url: url }));
                      setCreateThumbUploading(false);
                      e.target.value = '';
                    }}
                  />
                </label>
                {createFormData.thumbnail_url && (
                  <Button type="button" variant="ghost" size="icon" onClick={() => setCreateFormData(prev => ({ ...prev, thumbnail_url: null }))}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>

            {/* Additional images upload */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("productImages") || "Product Images"}</Label>
                <label>
                  <div className={`flex items-center gap-1 px-3 py-1.5 border rounded-md cursor-pointer hover:bg-muted text-sm transition-colors ${createImgUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Plus className="h-4 w-4" />
                    {createImgUploading ? 'Uploading...' : t("addImage") || "Add Image"}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={createImgUploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setCreateImgUploading(true);
                      const url = await uploadImageFile(file);
                      if (url) setCreateFormData(prev => ({ ...prev, images: [...(prev.images || []), url] }));
                      setCreateImgUploading(false);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
              {createFormData.images && createFormData.images.length > 0 && (
                <div className="grid grid-cols-4 gap-2">
                  {createFormData.images.map((imageUrl, index) => (
                    <div key={index} className="relative">
                      <img src={imageUrl} alt={`Product image ${index + 1}`} className="h-20 w-20 object-cover rounded border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      <Button type="button" variant="destructive" size="icon" className="absolute top-0 right-0 h-6 w-6" onClick={() => handleRemoveImage(index, true)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Featured */}
            <div className="flex items-center space-x-2">
              <Switch
                id="create-featured"
                checked={createFormData.is_featured}
                onCheckedChange={(checked) => setCreateFormData(prev => ({ ...prev, is_featured: checked }))}
              />
              <Label htmlFor="create-featured">{t("featured") || "Featured Product"}</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateDialogOpen(false);
              setCreateFormData({
                name: '',
                name_ar: '',
                description: '',
                description_ar: '',
                price: 0,
                discount_price: null,
                stock: 0,
                sku: '',
                barcode: '',
                category_id: 0,
                brand_id: '',
                status: 'active',
                is_featured: false,
                thumbnail_url: null,
                images: [],
              });
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              onClick={handleCreateProduct}
              disabled={createProductMutation.isPending}
            >
              {createProductMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("creating") || "Creating..."}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("createProduct") || "Create Product"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("editProduct") || "Edit Product"}</DialogTitle>
            <DialogDescription>
              {t("updateProductInformation") || "Update product information"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">{t("productName") || "Name"} *</Label>
                  <Input
                    id="edit-name"
                    value={editFormData.name || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t("enterProductName") || "Enter product name"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-name-ar">{t("productNameAr") || "Name (AR)"} *</Label>
                  <Input
                    id="edit-name-ar"
                    value={editFormData.name_ar || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                    placeholder={t("enterProductNameAr") || "Enter product name in Arabic"}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-description">{t("description") || "Description"}</Label>
                  <Textarea
                    id="edit-description"
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={t("enterDescription") || "Enter description"}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-description-ar">{t("descriptionAr") || "Description (AR)"}</Label>
                  <Textarea
                    id="edit-description-ar"
                    value={editFormData.description_ar || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, description_ar: e.target.value }))}
                    placeholder={t("enterDescriptionAr") || "Enter description in Arabic"}
                    rows={3}
                  />
                </div>
              </div>

              {/* Pricing & Inventory */}
              <div className="grid grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-price">{t("price") || "Price"} *</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    step="0.01"
                    value={editFormData.price || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-discount-price">{t("discountPrice") || "Discount Price"}</Label>
                  <Input
                    id="edit-discount-price"
                    type="number"
                    step="0.01"
                    value={editFormData.discount_price || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, discount_price: e.target.value ? parseFloat(e.target.value) : null }))}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-stock">{t("stock") || "Stock"} *</Label>
                  <Input
                    id="edit-stock"
                    type="number"
                    value={editFormData.stock || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                    placeholder="0"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("status") || "Status"}</Label>
                  <Select 
                    value={editFormData.status || 'active'} 
                    onValueChange={(value) => setEditFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t("active") || "Active"}</SelectItem>
                      <SelectItem value="inactive">{t("inactive") || "Inactive"}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* SKU & Barcode */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-sku">{t("sku") || "SKU"} *</Label>
                  <Input
                    id="edit-sku"
                    value={editFormData.sku || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, sku: e.target.value }))}
                    placeholder={t("enterSku") || "Enter SKU"}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-barcode">{t("barcode") || "Barcode"}</Label>
                  <Input
                    id="edit-barcode"
                    value={editFormData.barcode || ''}
                    onChange={(e) => setEditFormData(prev => ({ ...prev, barcode: e.target.value }))}
                    placeholder={t("enterBarcode") || "Enter barcode"}
                  />
                </div>
              </div>

              {/* Category & Brand */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("category") || "Category"} *</Label>
                  <Select 
                    value={editFormData.category_id?.toString() || ''} 
                    onValueChange={(value) => setEditFormData(prev => ({ ...prev, category_id: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("selectCategory") || "Select Category"} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id.toString()}>
                          {getDisplayName(category)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("brand") || "Brand"} *</Label>
                  <Select 
                    value={editFormData.brand_id || ''} 
                    onValueChange={(value) => setEditFormData(prev => ({ ...prev, brand_id: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("selectBrand") || "Select Brand"} />
                    </SelectTrigger>
                    <SelectContent>
                      {brands.map((brand) => (
                        <SelectItem key={brand.id} value={brand.id}>
                          {getDisplayName(brand)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Thumbnail upload */}
              <div className="space-y-2">
                <Label>{t("thumbnailUrl") || "Thumbnail Image"}</Label>
                <div className="flex items-center gap-3">
                  {editFormData.thumbnail_url && (
                    <img src={editFormData.thumbnail_url} alt="thumbnail" className="h-16 w-16 object-cover rounded border" />
                  )}
                  <label className="flex-1">
                    <div className={`flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-muted transition-colors ${editThumbUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                      <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {editThumbUploading ? 'Uploading...' : editFormData.thumbnail_url ? 'Change image' : 'Choose image'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={editThumbUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setEditThumbUploading(true);
                        const url = await uploadImageFile(file);
                        if (url) setEditFormData(prev => ({ ...prev, thumbnail_url: url }));
                        setEditThumbUploading(false);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {editFormData.thumbnail_url && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => setEditFormData(prev => ({ ...prev, thumbnail_url: null }))}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Additional images upload */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t("productImages") || "Product Images"}</Label>
                  <label>
                    <div className={`flex items-center gap-1 px-3 py-1.5 border rounded-md cursor-pointer hover:bg-muted text-sm transition-colors ${editImgUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                      <Plus className="h-4 w-4" />
                      {editImgUploading ? 'Uploading...' : t("addImage") || "Add Image"}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={editImgUploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setEditImgUploading(true);
                        const url = await uploadImageFile(file);
                        if (url) setEditFormData(prev => ({ ...prev, images: [...(prev.images || []), url] }));
                        setEditImgUploading(false);
                        e.target.value = '';
                      }}
                    />
                  </label>
                </div>
                {editFormData.images && editFormData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2">
                    {editFormData.images.map((imageUrl, index) => (
                      <div key={index} className="relative">
                        <img src={imageUrl} alt={`Product image ${index + 1}`} className="h-20 w-20 object-cover rounded border" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        <Button type="button" variant="destructive" size="icon" className="absolute top-0 right-0 h-6 w-6" onClick={() => handleRemoveImage(index, false)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Featured */}
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-featured"
                  checked={editFormData.is_featured || false}
                  onCheckedChange={(checked) => setEditFormData(prev => ({ ...prev, is_featured: checked }))}
                />
                <Label htmlFor="edit-featured">{t("featured") || "Featured Product"}</Label>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditDialogOpen(false);
              setEditFormData({});
              setSelectedProduct(null);
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={updateProductMutation.isPending}
            >
              {updateProductMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("saving") || "Saving..."}
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  {t("saveChanges") || "Save Changes"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Stock Dialog */}
      <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("updateStock") || "Update Stock"}</DialogTitle>
            <DialogDescription>
              {t("updateProductStock") || "Update the stock quantity for this product"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              <div>
                <Label>{t("productName") || "Product"}</Label>
                <p className="text-sm font-medium">{selectedProduct.name}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="stock-input">{t("stock") || "Stock"} *</Label>
                <Input
                  id="stock-input"
                  type="number"
                  value={stockValue}
                  onChange={(e) => setStockValue(e.target.value)}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsStockDialogOpen(false);
              setStockValue('');
              setSelectedProduct(null);
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button 
              onClick={handleSaveStock}
              disabled={updateStockMutation.isPending}
            >
              {updateStockMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("saving") || "Saving..."}
                </>
              ) : (
                <>
                  {t("saveChanges") || "Save Changes"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
